import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Icons } from '../../lib/Icons';
import { getSencoCohortAnalytics } from '../../lib/functions';
import { ALL_YEAR_GROUPS } from '../../lib/curriculumAssessment';

const PRESET_CHIPS = [
  { label: 'Year 3 EHCP count', yearGroup: 'Year 3', ehcpFilter: 'all', headlineMode: 'ehcp' },
  { label: 'Year 10 at expected %', yearGroup: 'Year 10', ehcpFilter: 'all', headlineMode: 'attainment' },
  { label: 'EHCP vs non-EHCP gap', yearGroup: '', ehcpFilter: 'all', gapView: true, headlineMode: 'default' },
];

export default function SencoCohortDataPanel({ embedded = false }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [payload, setPayload] = useState(null);
  const [yearGroups, setYearGroups] = useState([]);
  const [ehcpFilter, setEhcpFilter] = useState('all');
  const [subject, setSubject] = useState('');
  const [atExpectedOnly, setAtExpectedOnly] = useState(false);
  const [headlineMode, setHeadlineMode] = useState('default');

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const request = {};
      if (yearGroups.length > 0) request.yearGroups = yearGroups;
      if (ehcpFilter && ehcpFilter !== 'all') request.ehcpFilter = ehcpFilter;
      if (subject) request.subject = subject;
      if (atExpectedOnly) request.atExpectedOnly = true;

      const response = await getSencoCohortAnalytics(request);
      setPayload(response?.data || null);
    } catch (err) {
      setError(err?.message || 'Failed to load cohort analytics');
    } finally {
      setLoading(false);
    }
  }, [yearGroups, ehcpFilter, subject, atExpectedOnly]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const headline = useMemo(() => {
    if (!payload) return null;
    const { presetAnswers, summary, filters } = payload;

    if (filters?.yearGroups?.length === 1 && headlineMode === 'ehcp' && presetAnswers?.ehcpInYear) {
      const { yearGroup, count, total } = presetAnswers.ehcpInYear;
      return `${count} of ${total} pupils in ${yearGroup} have an EHCP`;
    }

    if (filters?.yearGroups?.length === 1 && presetAnswers?.senAtExpectedInYear) {
      const { yearGroup, pct, denominator } = presetAnswers.senAtExpectedInYear;
      if (denominator === 0) {
        return `No attainment data recorded yet for ${yearGroup}`;
      }
      if (headlineMode === 'attainment' || headlineMode === 'default') {
        return `${yearGroup}: ${pct}% at expected in all core subjects`;
      }
    }

    if (filters?.ehcpFilter === 'ehcp') {
      return `EHCP pupils at expected in all core subjects: ${summary.ehcpAtExpectedPct ?? 0}%`;
    }

    return `Students at expected in all core subjects: ${summary.atExpectedPct ?? 0}%`;
  }, [payload, headlineMode]);

  const toggleYearGroup = (yg) => {
    setYearGroups((prev) =>
      prev.includes(yg) ? prev.filter((y) => y !== yg) : [...prev, yg],
    );
  };

  const applyPreset = (preset) => {
    setHeadlineMode(preset.headlineMode || 'default');
    if (preset.gapView) {
      setYearGroups([]);
      setEhcpFilter('all');
      setSubject('');
      setAtExpectedOnly(false);
      return;
    }
    setYearGroups(preset.yearGroup ? [preset.yearGroup] : []);
    setEhcpFilter(preset.ehcpFilter || 'all');
    setSubject('');
    setAtExpectedOnly(false);
  };

  const exportCsv = () => {
    if (!payload) return;
    const rows = [
      ['name', 'yearGroup', 'hasEHCP', 'atExpectedOverall', 'subjects'],
      ...(payload.pupils || []).map((p) => [
        p.name,
        p.yearGroup,
        p.hasEHCP ? 'yes' : 'no',
        p.atExpectedOverall ? 'yes' : 'no',
        Object.entries(p.subjects || {})
          .map(([k, v]) => `${k}: ${v}`)
          .join('; '),
      ]),
      [''],
      ['yearGroup', 'total', 'ehcpCount', 'atExpectedPct'],
      ...(payload.byYearGroup || []).map((row) => [
        row.yearGroup,
        row.total,
        row.ehcpCount,
        row.atExpectedPct,
      ]),
    ];
    const csv = rows.map((r) => r.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `senco-cohort-data-${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <section className={embedded ? 'space-y-5' : 'rounded-2xl border border-gray-100 bg-white shadow-md'}>
      <div className={embedded ? '' : 'border-b border-gray-100 p-5'}>
        <div className="flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
          {!embedded && (
            <div>
              <h2 className="text-xl font-bold text-gray-900">Cohort Data</h2>
              <p className="mt-1 text-sm text-gray-600">
                EHCP counts, attainment by year group, and subject breakdowns.
              </p>
            </div>
          )}
          {embedded && <div className="flex-1" />}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={exportCsv}
              disabled={!payload}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              <Icons.ArrowDownTray className="h-4 w-4" />
              Export CSV
            </button>
            <button
              type="button"
              onClick={fetchData}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <Icons.ArrowPath className="h-4 w-4" />
              Refresh
            </button>
          </div>
        </div>

        <div className={`flex flex-wrap gap-2 ${embedded ? 'mt-4' : 'mt-4'}`}>
          {PRESET_CHIPS.map((preset) => (
            <button
              key={preset.label}
              type="button"
              onClick={() => applyPreset(preset)}
              className="rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1 text-xs font-medium text-indigo-800 hover:bg-indigo-100"
            >
              {preset.label}
            </button>
          ))}
        </div>
      </div>

      <div className={embedded ? 'space-y-5' : 'p-5 space-y-5'}>
        {error && (
          <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</p>
        )}

        <div className="grid grid-cols-1 gap-3 md:grid-cols-4">
          <div>
            <label className="text-xs font-medium text-gray-600">Year group</label>
            <div className="mt-1 flex flex-wrap gap-1 max-h-24 overflow-y-auto">
              {ALL_YEAR_GROUPS.map((yg) => (
                <button
                  key={yg}
                  type="button"
                  onClick={() => toggleYearGroup(yg)}
                  className={`rounded px-2 py-0.5 text-xs border ${
                    yearGroups.includes(yg)
                      ? 'bg-indigo-600 text-white border-indigo-600'
                      : 'bg-white text-gray-700 border-gray-300'
                  }`}
                >
                  {yg}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600">EHCP filter</label>
            <select
              className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
              value={ehcpFilter}
              onChange={(e) => setEhcpFilter(e.target.value)}
            >
              <option value="all">All SEND pupils</option>
              <option value="ehcp">EHCP only</option>
              <option value="non_ehcp">Non-EHCP only</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-gray-600">Subject filter</label>
            <select
              className="mt-1 w-full rounded-md border border-gray-300 px-2 py-1.5 text-sm"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            >
              <option value="">Any subject</option>
              <option value="Reading">Reading</option>
              <option value="Writing">Writing</option>
              <option value="Maths">Maths</option>
              <option value="Science">Science</option>
              <option value="English">English</option>
              <option value="CLL">CLL (EYFS)</option>
              <option value="MD">MD (EYFS)</option>
              <option value="PD">PD (EYFS)</option>
              <option value="PSED">PSED (EYFS)</option>
            </select>
          </div>
          <div className="flex items-end">
            <label className="flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={atExpectedOnly}
                onChange={(e) => setAtExpectedOnly(e.target.checked)}
                className="rounded border-gray-300"
              />
              At expected only
            </label>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-8">
            <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-indigo-600" />
          </div>
        ) : payload ? (
          <>
            {headline && (
              <div className="rounded-lg bg-slate-900 px-4 py-3 text-white">
                <p className="text-sm font-medium">{headline}</p>
                <p className="text-xs text-slate-300 mt-1">
                  Academic year {payload.academicYear} · {payload.cohortSize} pupils in scope
                  {payload.reconciliation && !payload.reconciliation.ok && (
                    <span className="block text-amber-700 text-xs mt-1">
                      Data quality warning: year-group totals ({payload.reconciliation.totalFromYearGroups}{' '}
                      pupils, {payload.reconciliation.ehcpFromYearGroups} EHCP) do not match cohort header (
                      {payload.reconciliation.cohortSize} pupils, {payload.reconciliation.ehcpCount} EHCP).
                    </span>
                  )}
                </p>
              </div>
            )}

            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="rounded-lg border border-gray-200 p-3 text-center">
                <div className="text-2xl font-bold text-indigo-600">{payload.summary?.ehcpCount ?? 0}</div>
                <div className="text-xs text-gray-600">EHCP pupils</div>
              </div>
              <div className="rounded-lg border border-gray-200 p-3 text-center">
                <div className="text-2xl font-bold text-emerald-600">{payload.summary?.atExpectedPct ?? 0}%</div>
                <div className="text-xs text-gray-600">Students at expected (all core)</div>
              </div>
              <div className="rounded-lg border border-gray-200 p-3 text-center">
                <div className="text-2xl font-bold text-blue-600">{payload.summary?.ehcpAtExpectedPct ?? 0}%</div>
                <div className="text-xs text-gray-600">EHCP at expected</div>
              </div>
              <div className="rounded-lg border border-gray-200 p-3 text-center">
                <div className="text-2xl font-bold text-purple-600">{payload.summary?.nonEhcpAtExpectedPct ?? 0}%</div>
                <div className="text-xs text-gray-600">Non-EHCP at expected</div>
              </div>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-2">By year group</h3>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 text-left text-xs text-gray-500 uppercase">
                      <th className="py-2 pr-4">Year</th>
                      <th className="py-2 pr-4">Total</th>
                      <th className="py-2 pr-4">EHCP</th>
                      <th className="py-2 pr-4">At expected</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(payload.byYearGroup || []).map((row) => (
                      <tr key={row.yearGroup} className="border-b border-gray-100">
                        <td className="py-2 pr-4 font-medium">{row.yearGroup}</td>
                        <td className="py-2 pr-4">{row.total}</td>
                        <td className="py-2 pr-4">{row.ehcpCount}</td>
                        <td className="py-2 pr-4">{row.atExpectedPct}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-2">Pupil drill-down</h3>
              <div className="overflow-x-auto max-h-64 overflow-y-auto">
                <table className="min-w-full text-sm">
                  <thead className="sticky top-0 bg-gray-50">
                    <tr className="text-left text-xs text-gray-500 uppercase">
                      <th className="py-2 px-2">Name</th>
                      <th className="py-2 px-2">Year</th>
                      <th className="py-2 px-2">EHCP</th>
                      <th className="py-2 px-2">At expected</th>
                      <th className="py-2 px-2">Subjects</th>
                      <th className="py-2 px-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {(payload.pupils || []).map((p) => (
                      <tr key={p.studentId} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-2 px-2 font-medium">{p.name}</td>
                        <td className="py-2 px-2">{p.yearGroup}</td>
                        <td className="py-2 px-2">{p.hasEHCP ? 'Yes' : 'No'}</td>
                        <td className="py-2 px-2">
                          {p.hasAttainmentData ? (
                            p.atExpectedOverall ? (
                              <span className="text-green-700">Yes</span>
                            ) : (
                              <span className="text-amber-700">Not yet</span>
                            )
                          ) : (
                            'Not recorded'
                          )}
                        </td>
                        <td className="py-2 px-2 text-xs text-gray-600">
                          {Object.entries(p.subjects || {})
                            .filter(([, v]) => v)
                            .map(([k, v]) => `${k}: ${v}`)
                            .join(' · ') || 'Not recorded'}
                        </td>
                        <td className="py-2 px-2">
                          <Link
                            to={`/student/${p.studentId}?tab=progress`}
                            className="text-indigo-600 hover:underline text-xs"
                          >
                            View
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </>
        ) : null}
      </div>
    </section>
  );
}
